/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import {
    adminLogin,
    adminLogout,
    forgotPassword,
    resetPassword,
} from '@/services/auth.service'
import { useAuthStore } from '@/store'
import type {
    LoginRequest,
    ForgotPasswordRequest,
    ResetPasswordRequest,
} from '@/types/auth'

export const useSignInMutation = () => {
    const { login, setLoading } = useAuthStore()
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['sign-in'],
        mutationFn: (payload: LoginRequest) => adminLogin(payload),
        onMutate: () => {
            setLoading(true)
        },
        onSuccess: response => {
            if (response.status === 'success' && response.data) {
                login(response.data)
                toast.success(response.message || 'Login successful')
            }
        },
        onError: (error: any) => {
            const data = error.response?.data as {
                message?: string
                errors?: string[]
            }
            const message =
                data?.message ||
                error.message ||
                'Unable to sign in with those credentials.'
            const errors = data?.errors

            // If there are errors in the array, show them
            if (errors && Array.isArray(errors) && errors.length > 0) {
                toast.error(message, {
                    description: errors.join('\n'),
                })
            } else {
                toast.error(message)
            }
        },
        onSettled: () => {
            setLoading(false)
            queryClient.invalidateQueries({ queryKey: ['auth'] })
        },
    })
}

export const useSignOutMutation = () => {
    const navigate = useNavigate()
    const { logout, setLoading, refreshToken } = useAuthStore()
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['sign-out'],
        mutationFn: () => adminLogout(refreshToken || undefined),
        onMutate: () => {
            setLoading(true)
        },
        onSuccess: () => {
            logout()
            queryClient.clear()
            toast.success('Logged out successfully')
            navigate('/auth/sign-in')
        },
        onError: (error: any) => {
            logout()
            queryClient.clear()
            console.error('Logout error:', error)
            toast.error('Logged out locally')
            navigate('/auth/sign-in')
        },
        onSettled: () => {
            setLoading(false)
        },
    })
}

export const useForgotPasswordMutation = () =>
    useMutation({
        mutationKey: ['forgot-password'],
        mutationFn: (payload: ForgotPasswordRequest) => forgotPassword(payload),
        onSuccess: response => {
            toast.success(
                response.message ||
                    'Password reset link has been sent to your email'
            )
        },
        onError: (error: any) => {
            const data = error.response?.data as {
                message?: string
                errors?: string[]
            }
            const message =
                data?.message ||
                error.message ||
                'Failed to send reset email. Please try again.'
            const errors = data?.errors

            // If there are errors in the array, show them
            if (errors && Array.isArray(errors) && errors.length > 0) {
                toast.error(message, {
                    description: errors.join('\n'),
                })
            } else {
                toast.error(message)
            }
        },
    })

export const useCreatePasswordMutation = () => {
    const navigate = useNavigate()

    return useMutation({
        mutationKey: ['create-password'],
        mutationFn: (payload: ResetPasswordRequest) => resetPassword(payload),
        onSuccess: response => {
            toast.success(
                response.message ||
                    'Password reset successful. Please login with your new password.'
            )
            // Navigate to login after 2 seconds
            setTimeout(() => {
                navigate('/auth/sign-in', {
                    state: { passwordReset: true },
                })
            }, 2000)
        },
        onError: (error: any) => {
            const data = error.response?.data as {
                message?: string
                errors?: string[]
            }
            const message =
                data?.message ||
                error.message ||
                'Failed to reset password. Please try again.'
            const errors = data?.errors

            // If there are errors in the array, show them
            if (errors && Array.isArray(errors) && errors.length > 0) {
                toast.error(message, {
                    description: errors.join('\n'),
                })
            } else {
                toast.error(message)
            }
        },
    })
}
